import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { StarAuthShell } from "@/pages/star/StarLayout";
import { STANDARDS } from "@/lib/standards";

export default function StudentRegister() {
  const { studentRegister } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", student_class: "", roll_number: "", guardian_email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await studentRegister(form);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Registration failed");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <StarAuthShell title="Registration Submitted">
        <div className="mt-6 text-center" data-testid="student-register-success">
          <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto" />
          <p className="mt-4 text-slate-600">Your account has been created and is <span className="font-semibold text-slate-800">pending admin approval</span>. Once approved, you can log in to view your stars.</p>
          <Button onClick={() => navigate("/student/login")} className="w-full mt-6 h-11 bg-blue-900 hover:bg-blue-950" data-testid="student-register-go-login">Go to Login</Button>
        </div>
      </StarAuthShell>
    );
  }

  return (
    <StarAuthShell title="Student Registration" subtitle="Create your account. Admin approval is required.">
      <Seo title="Student Registration" />
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="student-register-form">
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Full Name</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Student name" data-testid="student-reg-name" required />
        </div>
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Class / Standard</Label>
          <Select value={form.student_class} onValueChange={(v) => setForm((f) => ({ ...f, student_class: v }))} required>
            <SelectTrigger data-testid="student-reg-class"><SelectValue placeholder="Select your standard" /></SelectTrigger>
            <SelectContent>
              {STANDARDS.map((s) => <SelectItem key={s} value={s} data-testid={`student-reg-class-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Roll Number / Student ID</Label>
          <Input value={form.roll_number} onChange={set("roll_number")} placeholder="e.g. 1001" data-testid="student-reg-roll" required />
        </div>
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Parent / Guardian Email</Label>
          <Input type="email" value={form.guardian_email} onChange={set("guardian_email")} placeholder="parent@example.com" data-testid="student-reg-email" required />
        </div>
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Password</Label>
          <Input type="password" value={form.password} onChange={set("password")} placeholder="Choose a password" data-testid="student-reg-password" required />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-900 hover:bg-blue-950" data-testid="student-reg-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
        </Button>
      </form>
      <p className="mt-5 text-sm text-slate-600 text-center">
        Already have an account? <Link to="/student/login" className="font-semibold text-blue-900 hover:underline">Sign in</Link>
      </p>
    </StarAuthShell>
  );
}
